import { PAY_ESTIMATE, QSHEET } from "../_constants/actionConstants";

/*
 * State shape:
 * {
 *   estimateLists: {
 *     [projectId]: PayEstimateListDto[]
 *   },
 *   openEstimateDetails: {
 *     [tabId]: PayEstimateDetailDto
 *   },
 *   openQSheets: {
 *     [tabId]: QSheetDto
 *   },
 *   fetching: bool,
 *   updating: bool,
 *   error: null | string,
 * }
 */

const initialState = {
  estimateLists: {},
  openEstimateDetails: {},
  reportedItems: {},   // keyed by tabId: { loading: bool, items: [] }
  prepopulatingQSheets: {},   // keyed by tabId: bool
  openQSheets: {},
  fetching: false,
  updating: false,
  error: null,
};

export const reducer = (state = initialState, action) => {
  switch (action.type) {

    // ── Pay Estimate List ─────────────────────────────────────────────
    case PAY_ESTIMATE.FETCH_LIST:
      return { ...state, fetching: true, error: null };

    case PAY_ESTIMATE.FETCH_LIST_FULFILLED:
      return {
        ...state,
        fetching: false,
        estimateLists: {
          ...state.estimateLists,
          [action.projectId]: action.payload,
        },
      };

    case PAY_ESTIMATE.FETCH_LIST_REJECTED:
      return { ...state, fetching: false, error: "Failed to fetch pay estimate list." };

    // ── Pay Estimate Detail ───────────────────────────────────────────
    case PAY_ESTIMATE.FETCH_DETAIL:
      return { ...state, fetching: true, error: null };

    case PAY_ESTIMATE.FETCH_DETAIL_FULFILLED:
      return {
        ...state,
        fetching: false,
        openEstimateDetails: {
          ...state.openEstimateDetails,
          [action.tabId]: action.payload,
        },
      };

    case PAY_ESTIMATE.FETCH_DETAIL_REJECTED:
      return { ...state, fetching: false, error: "Failed to fetch pay estimate detail." };

    // ── Create Estimate ───────────────────────────────────────────────
    case PAY_ESTIMATE.CREATE:
      return { ...state, updating: true, error: null };

    case PAY_ESTIMATE.CREATE_FULFILLED:
      // Clear the list cache for the project so it refreshes
      return {
        ...state,
        updating: false,
        estimateLists: {
          ...state.estimateLists,
          [action.projectId]: undefined,
        },
      };

    case PAY_ESTIMATE.CREATE_REJECTED:
      return { ...state, updating: false, error: "Failed to create pay estimate." };

    // ── Close Estimate ────────────────────────────────────────────────
    case PAY_ESTIMATE.CLOSE:
      return { ...state, updating: true, error: null };

    case PAY_ESTIMATE.CLOSE_FULFILLED: {
      const detail = state.openEstimateDetails[action.tabId];
      return {
        ...state,
        updating: false,
        openEstimateDetails: {
          ...state.openEstimateDetails,
          [action.tabId]: detail ? { ...detail, isClosed: true } : detail,
        },
      };
    }

    case PAY_ESTIMATE.CLOSE_REJECTED:
      return { ...state, updating: false, error: "Failed to close pay estimate." };

    // ── Delete Estimate ───────────────────────────────────────────────
    case PAY_ESTIMATE.DELETE:
      return { ...state, updating: true, error: null };

    case PAY_ESTIMATE.DELETE_FULFILLED: {
      const list = state.estimateLists[action.projectId] || [];
      return {
        ...state,
        updating: false,
        estimateLists: {
          ...state.estimateLists,
          [action.projectId]: list.filter((e) => e.payEstimateId !== action.payEstimateId),
        },
      };
    }

    case PAY_ESTIMATE.DELETE_REJECTED:
      return { ...state, updating: false, error: "Failed to delete pay estimate." };

    // ── Reported Items ────────────────────────────────────────────────
    case PAY_ESTIMATE.FETCH_REPORTED_ITEMS:
      return {
        ...state,
        reportedItems: {
          ...state.reportedItems,
          [action.tabId]: { loading: true, items: [] },
        },
      };

    case PAY_ESTIMATE.FETCH_REPORTED_ITEMS_FULFILLED:
      return {
        ...state,
        reportedItems: {
          ...state.reportedItems,
          [action.tabId]: { loading: false, items: action.payload },
        },
      };

    case PAY_ESTIMATE.FETCH_REPORTED_ITEMS_REJECTED:
      return {
        ...state,
        reportedItems: {
          ...state.reportedItems,
          [action.tabId]: { loading: false, items: [] },
        },
      };

    // ── Prepopulate QSheets ───────────────────────────────────────────
    case PAY_ESTIMATE.PREPOPULATE_QSHEETS:
      return {
        ...state,
        prepopulatingQSheets: { ...state.prepopulatingQSheets, [action.tabId]: true },
      };

    case PAY_ESTIMATE.PREPOPULATE_QSHEETS_FULFILLED:
    case PAY_ESTIMATE.PREPOPULATE_QSHEETS_REJECTED:
      return {
        ...state,
        prepopulatingQSheets: { ...state.prepopulatingQSheets, [action.tabId]: false },
      };

    case PAY_ESTIMATE.CLOSE_TAB: {
      const { [action.tabId]: _removed, ...remainingDetails } = state.openEstimateDetails;
      return { ...state, openEstimateDetails: remainingDetails };
    }

    // ── QSheet ────────────────────────────────────────────────────────
    case QSHEET.FETCH:
      return { ...state, fetching: true, error: null };

    case QSHEET.FETCH_FULFILLED:
      return {
        ...state,
        fetching: false,
        openQSheets: {
          ...state.openQSheets,
          [action.tabId]: action.payload,
        },
      };

    case QSHEET.FETCH_REJECTED:
      return { ...state, fetching: false, error: "Failed to fetch QSheet." };

    case QSHEET.CREATE:
    case QSHEET.DELETE:
    case QSHEET.CALCULATE:
    case QSHEET.CHECK:
    case QSHEET.REVERT:
    case QSHEET.SAVE:
      return { ...state, updating: true, error: null };

    case QSHEET.CREATE_FULFILLED:
    case QSHEET.CALCULATE_FULFILLED:
    case QSHEET.CHECK_FULFILLED:
    case QSHEET.REVERT_FULFILLED:
    case QSHEET.SAVE_FULFILLED:
      return { ...state, updating: false };

    case QSHEET.DELETE_FULFILLED: {
      // Remove the deleted QSheet from any open estimate detail's qSheets list
      const updatedDetails = Object.fromEntries(
        Object.entries(state.openEstimateDetails).map(([tid, detail]) => [
          tid,
          detail?.qSheets
            ? { ...detail, qSheets: detail.qSheets.filter((q) => q.qSheetId !== action.qSheetId) }
            : detail,
        ])
      );
      // Also evict from openQSheets
      const { [action.tabId]: _removedQs, ...remainingQSheets } = state.openQSheets;
      return {
        ...state,
        updating: false,
        openEstimateDetails: updatedDetails,
        openQSheets: remainingQSheets,
      };
    }

    case QSHEET.CREATE_REJECTED:
    case QSHEET.DELETE_REJECTED:
    case QSHEET.CALCULATE_REJECTED:
    case QSHEET.CHECK_REJECTED:
    case QSHEET.REVERT_REJECTED:
    case QSHEET.SAVE_REJECTED:
      return { ...state, updating: false, error: "QSheet operation failed." };

    case QSHEET.CLOSE_TAB: {
      const { [action.tabId]: _removedQs, ...remainingQSheets } = state.openQSheets;
      return { ...state, openQSheets: remainingQSheets };
    }

    default:
      return state;
  }
};
